import React from 'react';
import { Check, Bot, Users, Trophy } from 'lucide-react';
import { Button } from './ui/button';

export const PlayModesSection = () => {
  const modes = [
    {
      id: 'treino',
      title: 'Treino',
      icon: Bot,
      color: 'green',
      features: ['Gratuito', 'Contra bots', 'Aprenda Truco'],
      cta: 'Treinar Agora'
    },
    {
      id: 'casual',
      title: 'Casual',
      icon: Users,
      color: 'purple',
      featured: true,
      features: ['Gratuito', 'Jogadores reais', 'Sem pressão'],
      cta: 'Jogar Casual'
    },
    {
      id: 'competitivo',
      title: 'Competitivo',
      icon: Trophy,
      color: 'green',
      features: ['Ranking', 'Eventos', 'Recompensas'],
      cta: 'Competir'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-[#05050A] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-green-primary/20 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-12">
          <p className="overline mb-2">3 Modos</p>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black font-archive text-white mb-3">
            Escolha seu jeito de jogar
          </h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-md mx-auto">
            Do iniciante ao profissional
          </p>
        </div>

        {/* Mobile: Horizontal Carousel */}
        <div className="lg:hidden">
          <div className="snap-carousel hidden-scrollbar -mx-4">
            {modes.map((mode) => {
              const Icon = mode.icon;
              const isGreen = mode.color === 'green';
              
              return (
                <div
                  key={mode.id}
                  data-testid={`mode-card-${mode.id}`}
                  className={`w-[260px] flex-shrink-0 first:ml-4 last:mr-4 ${mode.featured ? 'scale-[1.02]' : ''}`}
                >
                  <div className={`game-card p-5 rounded-2xl h-full relative ${
                    mode.featured 
                      ? 'border-purple-primary/60 bg-gradient-to-b from-purple-primary/20 to-transparent' 
                      : isGreen ? 'border-green-primary/30' : 'border-purple-primary/30'
                  }`}>
                    {/* Featured Badge */}
                    {mode.featured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-purple-primary text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                          Popular
                        </span>
                      </div>
                    )}

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      isGreen 
                        ? 'bg-gradient-to-br from-green-primary to-green-secondary' 
                        : 'bg-gradient-to-br from-purple-primary to-purple-light'
                    }`}>
                      <Icon className="text-white" size={24} />
                    </div>

                    {/* Title */}
                    <h3 className={`text-xl font-bold font-archive mb-4 ${
                      isGreen ? 'text-green-primary' : 'text-purple-light'
                    }`}>
                      {mode.title}
                    </h3>

                    {/* Features */}
                    <ul className="space-y-2 mb-5">
                      {mode.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            isGreen ? 'bg-green-primary/20' : 'bg-purple-primary/20'
                          }`}>
                            <Check className={isGreen ? 'text-green-primary' : 'text-purple-light'} size={12} />
                          </div>
                          <span className="text-sm text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Button 
                      data-testid={`mode-cta-${mode.id}`}
                      className={`w-full py-3 rounded-xl font-bold text-sm ${
                        mode.featured 
                          ? 'bg-purple-primary hover:bg-purple-light text-white' 
                          : isGreen 
                            ? 'bg-green-primary/20 hover:bg-green-primary/30 text-green-primary border border-green-primary/30' 
                            : 'bg-purple-primary/20 hover:bg-purple-primary/30 text-purple-light border border-purple-primary/30'
                      }`}
                    >
                      {mode.cta}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-gray-500 mt-4">
            ← Deslize para ver mais →
          </p>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden lg:grid grid-cols-3 gap-6 max-w-4xl mx-auto">
          {modes.map((mode) => {
            const Icon = mode.icon;
            const isGreen = mode.color === 'green';
            
            return (
              <div
                key={mode.id}
                data-testid={`mode-card-${mode.id}-desktop`}
                className={`relative ${mode.featured ? '-mt-4 mb-4' : ''}`}
              >
                <div className={`game-card p-6 rounded-2xl h-full relative transition-transform hover:scale-105 ${
                  mode.featured 
                    ? 'border-purple-primary/60 bg-gradient-to-b from-purple-primary/20 to-transparent shadow-lg shadow-purple-primary/20' 
                    : isGreen ? 'border-green-primary/30' : 'border-purple-primary/30'
                }`}>
                  {/* Featured Badge */}
                  {mode.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-purple-primary text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                        Popular
                      </span>
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5 ${
                    isGreen 
                      ? 'bg-gradient-to-br from-green-primary to-green-secondary' 
                      : 'bg-gradient-to-br from-purple-primary to-purple-light'
                  }`}>
                    <Icon className="text-white" size={28} />
                  </div>

                  {/* Title */}
                  <h3 className={`text-2xl font-bold font-archive text-center mb-5 ${
                    isGreen ? 'text-green-primary' : 'text-purple-light'
                  }`}>
                    {mode.title}
                  </h3>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {mode.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 justify-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          isGreen ? 'bg-green-primary/20' : 'bg-purple-primary/20'
                        }`}>
                          <Check className={isGreen ? 'text-green-primary' : 'text-purple-light'} size={14} />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button 
                    data-testid={`mode-cta-${mode.id}-desktop`}
                    className={`w-full py-4 rounded-xl font-bold ${
                      mode.featured 
                        ? 'btn-neon' 
                        : isGreen 
                          ? 'bg-green-primary/20 hover:bg-green-primary/30 text-green-primary border border-green-primary/30' 
                          : 'bg-purple-primary/20 hover:bg-purple-primary/30 text-purple-light border border-purple-primary/30'
                    }`}
                  >
                    {mode.cta}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
